// Device keys (spec section 6.1): every install holds a P-256 ECDH pair whose
// private half never leaves WebCrypto, and registers the public half with the
// API as a device. Device keys carry no authority (decision 23); they exist so
// MK can be wrapped to a device. An approved device wraps MK to a new device by
// ECDH with an ephemeral pair against the new device's public key, HKDF to an
// AES-256-GCM key, and `{ v: 1, ephemeralPublicKey, iv, wrapped }`, which the
// server stores against the device; only that device's private key opens it.
//
// The wrapping is bound to the member, the MK generation and the recipient
// key, so a wrapping served against the wrong device or the wrong `keyId`
// fails to open instead of being adopted. What `v: 1` does NOT defend against
// is the server substituting the pending device's public key before the
// approver reads it — and that is what `v: 2` closes (#1693, decision 15): the
// out-of-band secret S goes into the HKDF as `ECDH shared secret || S`, so a
// substituted public key yields a wrapping nobody can open. S travels by
// camera or by eye and never reaches the server (`approval-secret.js`).
//
// BOTH VERSIONS UNWRAP, only `v: 2` is written. An approval already in flight
// when the app updates was made under `v: 1` and still has to land, so the
// unwrap keeps it; nothing new is written that way once a caller passes a
// secret.
import { approvalSecretCandidates } from './approval-secret';
import { hashCanonical } from './canonical';
import { base64ToBytes, bytesToBase64, concatBytes, utf8Encode } from './encoding';
import { assertKeyId, assertMemberId } from './identifiers';
import { MASTER_KEY_BYTES, assertMasterKey } from './master-key';
import { getSubtle, hkdfKey, randomBytes } from './webcrypto';

/** Written before #1693, and still unwrapped: no out-of-band secret. */
export const DEVICE_WRAP_VERSION_V1 = 1;
/** Written now: the wrapping key folds in the out-of-band secret S. */
export const DEVICE_WRAP_VERSION = 2;
const SUPPORTED_WRAP_VERSIONS = Object.freeze([DEVICE_WRAP_VERSION_V1, DEVICE_WRAP_VERSION]);
const IV_BYTES = 12;
const ECDH_P256 = Object.freeze({ name: 'ECDH', namedCurve: 'P-256' });
const WRAP_INFO = 'device-wrap';
const WRAP_AAD_LABEL = 'device-wrap';

export const DEVICE_WRAP_ERRORS = Object.freeze({
  UNKNOWN_VERSION: 'UNKNOWN_VERSION',
  AUTH_FAILED: 'AUTH_FAILED',
  MALFORMED: 'MALFORMED',
});

const ERROR_MESSAGES = Object.freeze({
  UNKNOWN_VERSION: 'This device wrapping was made by a newer app version',
  AUTH_FAILED: 'This device wrapping is not for this device',
  MALFORMED: 'This device wrapping is not valid',
});

export class DeviceWrapError extends Error {
  /**
   * @param {keyof typeof DEVICE_WRAP_ERRORS} code
   * @param {string} [detail]
   */
  constructor(code, detail) {
    super(detail ? `${ERROR_MESSAGES[code]}: ${detail}` : ERROR_MESSAGES[code]);
    this.name = 'DeviceWrapError';
    this.code = code;
  }
}

/**
 * @param {CryptoKey} publicKey
 * @returns {Promise<string>} SPKI, standard base64: the form the API stores as `publicKey`
 */
export async function exportPublicKey(publicKey) {
  return bytesToBase64(new Uint8Array(await getSubtle().exportKey('spki', publicKey)));
}

/**
 * @returns {Promise<{ privateKey: CryptoKey, publicKey: string }>} the private half
 *   is non-extractable and can only derive; the public half is base64 SPKI
 */
export async function generateDeviceKeyPair() {
  const pair = await getSubtle().generateKey(ECDH_P256, false, ['deriveBits']);
  return { privateKey: pair.privateKey, publicKey: await exportPublicKey(pair.publicKey) };
}

/**
 * @param {string} publicKey base64 SPKI
 * @returns {Uint8Array}
 */
function decodePublicKey(publicKey) {
  try {
    return base64ToBytes(publicKey);
  } catch (error) {
    throw new DeviceWrapError(DEVICE_WRAP_ERRORS.MALFORMED, 'public key is not base64');
  }
}

/**
 * @param {string} publicKey base64 SPKI
 * @returns {Promise<CryptoKey>}
 */
export async function importDevicePublicKey(publicKey) {
  const spki = decodePublicKey(publicKey);
  try {
    return await getSubtle().importKey('spki', spki, ECDH_P256, true, []);
  } catch (error) {
    throw new DeviceWrapError(DEVICE_WRAP_ERRORS.MALFORMED, 'public key is not P-256 SPKI');
  }
}

/**
 * ECDH shared secret, then HKDF-SHA256 to the AES-256-GCM wrapping key.
 *
 * THE SECRET GOES IN THE IKM, not the salt or the info: the plan writes it as
 * `HKDF(ECDH shared secret || S)`, and it is keying material rather than a
 * label. With no secret this is `v: 1`'s derivation, byte for byte — which is
 * what lets an approval made before the update still open.
 *
 * @param {CryptoKey} privateKey
 * @param {CryptoKey} publicKey
 * @param {KeyUsage[]} usages
 * @param {Uint8Array} [secret] the out-of-band S, for `v: 2`
 * @returns {Promise<CryptoKey>}
 */
async function deriveWrappingKey(privateKey, publicKey, usages, secret) {
  const shared = new Uint8Array(
    await getSubtle().deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256),
  );
  return hkdfKey({
    ikm: secret && secret.length ? concatBytes(shared, secret) : shared,
    salt: new Uint8Array(0),
    info: WRAP_INFO,
    algorithm: { name: 'AES-GCM', length: 256 },
    usages,
  });
}

/**
 * Binds the wrapping to the member, the MK generation and the recipient's key
 * bytes (not their base64 rendering, which a transport is free to re-encode).
 *
 * @param {number} memberId
 * @param {string} keyId
 * @param {string} devicePublicKey base64 SPKI
 * @returns {Uint8Array}
 */
function wrapAad(memberId, keyId, devicePublicKey) {
  return concatBytes(
    utf8Encode(`${WRAP_AAD_LABEL}:${memberId}:${keyId}:`),
    decodePublicKey(devicePublicKey),
  );
}

/**
 * @param {object} args
 * @param {Uint8Array} args.mk
 * @param {string} args.keyId MK's generation
 * @param {number} args.memberId
 * @param {string} args.devicePublicKey the recipient's base64 SPKI, as registered
 * @param {Uint8Array} [args.secret] the out-of-band S the approver read off the
 *   pending device (`approval-secret.js`). With it the wrapping is `v: 2` and
 *   the substitution of decision 15 is closed; without it `v: 1`, which is what
 *   an approver on a build that cannot read S still writes. The screens are
 *   what make it non-optional in practice: Approve stays disabled until a scan
 *   or a typed code succeeds.
 * @returns {Promise<{ v: number, ephemeralPublicKey: string, iv: string, wrapped: string }>}
 */
export async function wrapMasterKeyToDevice({ mk, keyId, memberId, devicePublicKey, secret }) {
  assertMasterKey(mk);
  assertKeyId(keyId);
  assertMemberId(memberId);
  const subtle = getSubtle();
  const recipient = await importDevicePublicKey(devicePublicKey);
  const ephemeral = await subtle.generateKey(ECDH_P256, false, ['deriveBits']);
  const key = await deriveWrappingKey(ephemeral.privateKey, recipient, ['encrypt'], secret);
  const iv = randomBytes(IV_BYTES);
  const wrapped = await subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: wrapAad(memberId, keyId, devicePublicKey) },
    key,
    mk,
  );
  return {
    v: secret && secret.length ? DEVICE_WRAP_VERSION : DEVICE_WRAP_VERSION_V1,
    ephemeralPublicKey: await exportPublicKey(ephemeral.publicKey),
    iv: bytesToBase64(iv),
    wrapped: bytesToBase64(new Uint8Array(wrapped)),
  };
}

/**
 * @param {unknown} wrapping
 * @returns {{ ephemeralPublicKey: string, iv: Uint8Array, wrapped: Uint8Array }}
 */
function decodeWrapping(wrapping) {
  if (wrapping === null || typeof wrapping !== 'object') {
    throw new DeviceWrapError(DEVICE_WRAP_ERRORS.MALFORMED, 'not an object');
  }
  if (!Number.isInteger(wrapping.v)) {
    throw new DeviceWrapError(DEVICE_WRAP_ERRORS.MALFORMED, 'no version');
  }
  if (!SUPPORTED_WRAP_VERSIONS.includes(wrapping.v)) {
    throw new DeviceWrapError(DEVICE_WRAP_ERRORS.UNKNOWN_VERSION);
  }
  if (typeof wrapping.ephemeralPublicKey !== 'string') {
    throw new DeviceWrapError(DEVICE_WRAP_ERRORS.MALFORMED, 'no ephemeral public key');
  }
  let iv;
  let wrapped;
  try {
    iv = base64ToBytes(wrapping.iv);
    wrapped = base64ToBytes(wrapping.wrapped);
  } catch (error) {
    throw new DeviceWrapError(DEVICE_WRAP_ERRORS.MALFORMED, 'iv or wrapped is not base64');
  }
  if (iv.length !== IV_BYTES) throw new DeviceWrapError(DEVICE_WRAP_ERRORS.MALFORMED, 'iv length');
  return { v: wrapping.v, ephemeralPublicKey: wrapping.ephemeralPublicKey, iv, wrapped };
}

/**
 * The candidates for a `v: 2` unwrap, or none when this device has no secret to
 * offer. Never throws: a missing or malformed secret here is not a programming
 * error to surface, it is simply nothing that can open the wrapping, and the
 * caller's AUTH_FAILED says exactly that.
 *
 * @param {unknown} secret
 * @returns {Uint8Array[]}
 */
function approvalSecretCandidatesOrNone(secret) {
  try {
    return approvalSecretCandidates(secret).filter(candidate => candidate.length);
  } catch (error) {
    return [];
  }
}

/**
 * @param {object} args
 * @param {unknown} args.wrapping `thisDevice.wrappedMk` from the status response
 * @param {number} args.memberId
 * @param {string} args.keyId the bundle's `keyId` from the same response
 * @param {CryptoKey} args.devicePrivateKey this install's private half
 * @param {string} args.devicePublicKey this install's base64 SPKI, as registered
 * @param {Uint8Array} [args.secret] the S THIS device generated and displayed.
 *   Required to open a `v: 2` wrapping and ignored for a `v: 1` one.
 * @returns {Promise<Uint8Array>} MK
 */
export async function unwrapMasterKeyFromDevice({
  wrapping,
  memberId,
  keyId,
  devicePrivateKey,
  devicePublicKey,
  secret,
}) {
  assertKeyId(keyId);
  assertMemberId(memberId);
  const { v, ephemeralPublicKey, iv, wrapped } = decodeWrapping(wrapping);
  const ephemeral = await importDevicePublicKey(ephemeralPublicKey);
  const aad = wrapAad(memberId, keyId, devicePublicKey);

  // WHY A LIST. `v: 1` has one derivation and no secret. `v: 2` has one secret
  // in two carriers — the QR's sixteen bytes and the typed code's first ten —
  // and nothing tells this device which the approver used, so it offers both
  // rather than guessing (`approval-secret.js` says why that is safe: a wrong
  // candidate fails the GCM tag and is indistinguishable from any other wrong
  // key). A `v: 2` wrapping with no secret to try is a state the screens do not
  // produce, and it falls through to AUTH_FAILED like any other key that does
  // not open it.
  const candidates =
    v === DEVICE_WRAP_VERSION_V1 ? [undefined] : approvalSecretCandidatesOrNone(secret);

  for (const candidate of candidates) {
    const key = await deriveWrappingKey(devicePrivateKey, ephemeral, ['decrypt'], candidate);
    let mk;
    try {
      mk = new Uint8Array(
        await getSubtle().decrypt({ name: 'AES-GCM', iv, additionalData: aad }, key, wrapped),
      );
    } catch (error) {
      continue;
    }
    if (mk.length !== MASTER_KEY_BYTES) throw new DeviceWrapError(DEVICE_WRAP_ERRORS.AUTH_FAILED);
    return mk;
  }
  throw new DeviceWrapError(DEVICE_WRAP_ERRORS.AUTH_FAILED);
}

/**
 * The `wrappedMkHash` an approve signature carries: SHA-256 of the canonical
 * wrapping, base64url (API contract section 5).
 *
 * @param {{ v: number, ephemeralPublicKey: string, iv: string, wrapped: string }} wrapping
 * @returns {Promise<string>}
 */
export function wrappedMkHash({ v, ephemeralPublicKey, iv, wrapped }) {
  return hashCanonical({ v, ephemeralPublicKey, iv, wrapped });
}
